import React, { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Order } from 'models/order';
import { restorationPriceRows } from 'utils/restoration-price-rows';
import { Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { formatCurrency, formatNumber, formatPercentage } from 'utils/format';
import TableCellWithHighLighting from 'components/with-hith-light-components/table-cell-with-highlighting.component';

type PriceSpecificationSectionWithHighlightingProps = {
    order: Order;
    currency: string;
};

const PriceSpecificationSectionWithHighlighting: FC<PriceSpecificationSectionWithHighlightingProps> = ({
    order,
    currency,
}: PriceSpecificationSectionWithHighlightingProps) => {
    const { t: translate } = useTranslation(['orders', 'common']);
    const { priceSpec, deletedPriceSpec, isDiscountedPrice } = useMemo(() => {
        const currentRows = restorationPriceRows(order?.restorations);
        // Rows keep their _id across versions; without an original version nothing is highlighted.
        const originalRows = order?.originalOrder
            ? restorationPriceRows(order.originalOrder.restorations)
            : currentRows;
        const originalById = new Map(originalRows.map((row) => [row._id, row]));
        const currentIds = new Set(currentRows.map((row) => row._id));
        return {
            priceSpec: currentRows.map((row) => ({ ...row, original: originalById.get(row._id) })),
            deletedPriceSpec: originalRows.filter((row) => !currentIds.has(row._id)),
            isDiscountedPrice: currentRows.some(
                (row) => !!row.originalUnitPrice && row.originalUnitPrice !== row.unitPrice,
            ),
        };
    }, [order]);

    return (
        <TableContainer>
            <Table size="small">
                <TableHead sx={{ borderBottom: 'none' }}>
                    <TableRow>
                        <TableCell>{translate('common:DESCRIPTION')}</TableCell>
                        <TableCell align="right">{translate('orders:QUANTITY')}</TableCell>
                        <TableCell align="right">{translate('orders:UNIT_PRICE')}</TableCell>
                        {isDiscountedPrice && (
                            <TableCell align="right">{translate('orders:DISCOUNT_PERCENT')}</TableCell>
                        )}
                        <TableCell align="right">{translate('orders:VAT')}</TableCell>
                        <TableCell align="right">{translate('orders:TOTAL')}</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {priceSpec.map((priceSpec) => {
                        const { original } = priceSpec;
                        const actualPrice = priceSpec.originalUnitPrice;
                        return (
                            <TableRow key={priceSpec._id}>
                                {priceSpec.kind === 'ACTION' ? (
                                    <TableCellWithHighLighting
                                        originalData={original?.description ?? ''}
                                        data={priceSpec.description}
                                        rightAdornment={
                                            <>
                                                {priceSpec.quotable ? (
                                                    <Chip label={translate('common:QUOTABLE')} size="small" />
                                                ) : undefined}{' '}
                                                {priceSpec.isTioTandvard ? (
                                                    <Chip label={translate('orders:TIOTANDVARD_PRICE')} size="small" />
                                                ) : undefined}
                                            </>
                                        }
                                    ></TableCellWithHighLighting>
                                ) : (
                                    <TableCellWithHighLighting
                                        originalData={original?.description ?? ''}
                                        data={priceSpec.description}
                                    ></TableCellWithHighLighting>
                                )}
                                <TableCellWithHighLighting
                                    originalData={original ? formatNumber(original.quantity) : ''}
                                    data={formatNumber(priceSpec.quantity)}
                                    align="right"
                                ></TableCellWithHighLighting>
                                <TableCellWithHighLighting
                                    originalData={
                                        original
                                            ? formatCurrency(parseFloat(original.unitPrice.toFixed(2)), currency)
                                            : ''
                                    }
                                    data={
                                        priceSpec.hasPrice
                                            ? formatCurrency(parseFloat(priceSpec.unitPrice.toFixed(2)), currency)
                                            : translate('orders:NO_PRICE')
                                    }
                                    align="right"
                                    sx={{ whiteSpace: 'nowrap' }}
                                ></TableCellWithHighLighting>
                                {isDiscountedPrice && actualPrice ? (
                                    <TableCell sx={{ whiteSpace: 'nowrap' }} align="right">
                                        {actualPrice > priceSpec.unitPrice &&
                                        priceSpec.unitPrice !== 0 &&
                                        !order.isWarrantyOrder
                                            ? formatPercentage((actualPrice - priceSpec.unitPrice) / actualPrice)
                                            : '0%'}
                                    </TableCell>
                                ) : isDiscountedPrice && !actualPrice ? (
                                    <TableCell sx={{ whiteSpace: 'nowrap' }} align="right">
                                        0%
                                    </TableCell>
                                ) : undefined}
                                <TableCellWithHighLighting
                                    originalData={
                                        original ? formatCurrency(parseFloat(original.vat.toFixed(2)), currency) : ''
                                    }
                                    data={formatCurrency(parseFloat(priceSpec.vat.toFixed(2)), currency)}
                                    align="right"
                                    sx={{ whiteSpace: 'nowrap' }}
                                ></TableCellWithHighLighting>
                                <TableCell sx={{ whiteSpace: 'nowrap' }} align="right">
                                    {formatCurrency(priceSpec.totalWithVat, currency)}
                                </TableCell>
                            </TableRow>
                        );
                    })}
                    {deletedPriceSpec.map((priceSpec) => (
                        <TableRow
                            sx={{
                                color: '#ff0101',
                                fontWeight: 'bold',
                                textDecoration: 'line-through',
                            }}
                            key={priceSpec._id}
                        >
                            <TableCell>{priceSpec.description}</TableCell>
                            <TableCell align="right">{priceSpec.quantity}</TableCell>
                            <TableCell sx={{ whiteSpace: 'nowrap' }} align="right">
                                {formatCurrency(parseFloat(priceSpec.unitPrice.toFixed(2)), currency)}
                            </TableCell>
                            {isDiscountedPrice ? <TableCell /> : undefined}
                            <TableCell sx={{ whiteSpace: 'nowrap' }} align="right">
                                {formatCurrency(parseFloat(priceSpec.vat.toFixed(2)), currency)}
                            </TableCell>
                            <TableCell sx={{ whiteSpace: 'nowrap' }} align="right">
                                {formatCurrency(priceSpec.totalWithVat, currency)}
                            </TableCell>
                        </TableRow>
                    ))}
                    <TableRow sx={{ borderTop: '2px solid #bbb' }}>
                        <TableCell colSpan={isDiscountedPrice ? 5 : 4} align="right" sx={{ borderBottom: 0 }}>
                            <strong>{translate('orders:GRAND_TOTAL')}</strong>
                        </TableCell>
                        <TableCell align="right" sx={{ whiteSpace: 'nowrap', borderBottom: 0 }}>
                            <strong>
                                {formatCurrency(
                                    (order.totalAmount?.totalPrice || 0) + (order.totalAmount?.vatAmount || 0),
                                    currency,
                                )}
                            </strong>
                        </TableCell>
                    </TableRow>
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default PriceSpecificationSectionWithHighlighting;
